/**
 * The React Query cache → Sentry guard chains (extracted from App.tsx so the
 * guard ORDER and coverage are unit-testable — src/lib/__tests__/
 * queryCacheGuards.test.ts pins both).
 *
 * This is the load-bearing Sentry hygiene layer: each guard retired a real
 * noise class (transient network, constraint violations #23505, expected RPC
 * rejections #3472, auth races / dead sessions #3458, RPC permission denials
 * #3414). Precedence matters — e.g. a denial seen while the session is dead is
 * an artifact of auth loss and must be owned by the heal, not re-reported.
 * Reordering or dropping a guard silently reopens a closed noise class, so any
 * change here must move the pinning test in the same diff.
 */
import * as Sentry from "@sentry/react";
import {
  isTransientNetworkError,
  isConstraintViolationError,
  isNestAuthError,
  isCodedNestDomainError,
  isRpcPermissionDenialError,
  isExpectedRpcRejectionError,
  isHatifUpstreamError,
} from "@/lib/networkErrors";
import { healIfSessionDead } from "@/lib/sessionHeal";

/**
 * Normalizes errors to proper Error instances for Sentry.
 * Supabase and other API errors may be plain objects, which Sentry can't handle properly.
 */
export const normalizeError = (error: unknown): Error => {
  // If it's already an Error instance, return as-is
  if (error instanceof Error) {
    return error;
  }

  // If it's an object with error properties (like Supabase errors)
  if (error && typeof error === "object") {
    const errorObj = error as Record<string, unknown>;
    const message = errorObj.message as string || "An error occurred";
    const normalizedError = new Error(message);

    // Preserve original error properties as a structured object
    Object.assign(normalizedError, { originalError: errorObj });

    return normalizedError;
  }

  // Fallback for primitive values
  return new Error(String(error));
};

/** Structural slice of react-query's Query that the guard chain reads. */
export interface QueryLike {
  queryKey?: readonly unknown[];
  state: { status: string };
}

/** Structural slice of react-query's Mutation that the guard chain reads. */
export interface MutationLike {
  options: { mutationKey?: readonly unknown[] };
}

export const queryCacheOnError = (error: unknown, query: QueryLike): void => {
  // Don't report transient network errors to Sentry - they're infrastructure issues
  if (isTransientNetworkError(error)) {
    console.warn('[Network Error] Transient network error detected:', error);
    return;
  }

  // Don't report auth race errors — these fire when companyId is still truthy for
  // one render cycle after the GoTrue session is cleared (signout / refresh failure).
  // The query self-heals once the auth state propagates; no user-visible breakage.
  // If the session is actually DEAD (not a race), healIfSessionDead finishes the
  // sign-out so the tab doesn't stay stranded on a broken dashboard.
  if (isNestAuthError(error)) {
    void healIfSessionDead("query-cache");
    return;
  }

  // A coded Nest domain rejection (4xx + machine code from the bilingual
  // AppError envelope, e.g. accounting-002 "no connection yet") is the server
  // refusing as designed; the UI toasts the ar/en message. Downgrade to a
  // fingerprinted warning per code — observable, not an exception (#3659).
  if (isCodedNestDomainError(error)) {
    const code = (error as { code?: string }).code ?? "uncoded";
    const status = (error as { status?: number }).status;
    const queryKey = query.queryKey?.[0]?.toString() || "unknown";
    Sentry.captureMessage(`nest-domain-rejection: ${code}`, {
      level: "warning",
      fingerprint: ["nest-domain-rejection", code],
      tags: { source: "react-query", queryKey, code, status: String(status) },
    });
    return;
  }

  // An RPC authorization denial (42501 / bare 'not authorized') is the guard
  // working as designed — downgrade to a fingerprinted warning instead of an
  // uncategorised exception (#3414), keeping denial spikes observable without
  // the `Error: not authorized` noise class. With no live session the denial is
  // an artifact of auth loss (queries executing as `anon`), not a permission
  // bug — the heal owns that signal and routes the user to /auth.
  if (isRpcPermissionDenialError(error)) {
    const queryKey = query.queryKey?.[0]?.toString() || 'unknown';
    void healIfSessionDead("query-cache").then((deadSession) => {
      if (deadSession) return;
      Sentry.captureMessage(`rpc-permission-denial: ${queryKey}`, {
        level: "warning",
        fingerprint: ["rpc-permission-denial", queryKey],
        tags: { source: "react-query", queryKey },
      });
    });
    return;
  }

  // Hatif upstream-dependency failure (424 config fault / 502+ outage) — the
  // UI already degrades to its fallback state (#3592/#3593). Downgrade to a
  // fingerprinted warning so a sustained Voxa outage or a broken per-company
  // WABA config stays visible without the exception noise class.
  if (isHatifUpstreamError(error)) {
    const queryKey = query.queryKey?.[0]?.toString() || 'unknown';
    const status = (error as { status?: number }).status;
    Sentry.captureMessage(`hatif-upstream: ${queryKey}`, {
      level: "warning",
      fingerprint: ["hatif-upstream", queryKey],
      tags: { source: "react-query", queryKey, status: String(status) },
    });
    return;
  }

  const normalizedError = normalizeError(error);
  Sentry.captureException(normalizedError, {
    tags: {
      source: "react-query",
      queryKey: query.queryKey?.[0]?.toString() || 'unknown',
    },
    contexts: {
      errorDetails: error && typeof error === "object" ? (error as unknown as Record<string, unknown>) : { rawError: String(error) },
      query: {
        queryKey: query.queryKey,
        state: query.state.status,
      },
    },
  });
};

export const mutationCacheOnError = (error: unknown, _variables: unknown, _context: unknown, mutation: MutationLike): void => {
  // Don't report transient network errors to Sentry
  if (isTransientNetworkError(error)) {
    console.warn('[Network Error] Transient network error detected in mutation:', error);
    return;
  }
  // Don't report constraint violations — these are user input errors handled locally
  if (isConstraintViolationError(error)) {
    return;
  }
  // Expected RPC business-rule rejections (#3472) — the guarded RPC refused
  // in a success:false envelope (e.g. target_is_super_admin) and the dialog
  // throws only so its onError can toast the bilingual message. Working as
  // designed; capturing the throw is pure noise.
  if (isExpectedRpcRejectionError(error)) {
    return;
  }
  // Don't report auth race errors — mirrors the queryCache guard above. A mutation
  // submitted at the moment the GoTrue session is cleared self-heals once auth propagates.
  if (isNestAuthError(error)) {
    void healIfSessionDead("mutation-cache");
    return;
  }

  // Coded Nest domain rejection — mirrors the queryCache guard above. The
  // super-admin accounting mutations (test/upsert with a wrong key) live here.
  if (isCodedNestDomainError(error)) {
    const code = (error as { code?: string }).code ?? "uncoded";
    const status = (error as { status?: number }).status;
    const mutationKey = mutation.options.mutationKey?.[0]?.toString() || "mutation";
    Sentry.captureMessage(`nest-domain-rejection: ${code}`, {
      level: "warning",
      fingerprint: ["nest-domain-rejection", code],
      tags: { source: "react-query-mutation", mutationKey, code, status: String(status) },
    });
    return;
  }

  // RPC authorization denial — mirrors the queryCache guard above (#3414).
  // Attributed per mutationKey when the caller sets one (falls back to the
  // flat "mutation" bucket otherwise — most mutations don't carry a key).
  // The denial message names the denied table/RPC, so tag it — without it the
  // flat bucket can't say WHICH mutation was denied (#3696).
  if (isRpcPermissionDenialError(error)) {
    const mutationKey = mutation.options.mutationKey?.[0]?.toString() || "mutation";
    const denialMessage = String((error as { message?: unknown }).message ?? "").slice(0, 200);
    void healIfSessionDead("mutation-cache").then((deadSession) => {
      if (deadSession) return;
      Sentry.captureMessage(`rpc-permission-denial: ${mutationKey}`, {
        level: "warning",
        fingerprint: ["rpc-permission-denial", mutationKey],
        tags: { source: "react-query-mutation", mutationKey, denialMessage },
      });
    });
    return;
  }

  // Hatif upstream-dependency failure — mirrors the queryCache guard above.
  // A send-template or tag mutation failing because Voxa is down (502) or the
  // company's WABA config is broken (424) is already surfaced to the user by
  // the caller's onError toast; the exception capture was the noise.
  if (isHatifUpstreamError(error)) {
    const mutationKey = mutation.options.mutationKey?.[0]?.toString() || "mutation";
    const status = (error as { status?: number }).status;
    Sentry.captureMessage(`hatif-upstream: ${mutationKey}`, {
      level: "warning",
      fingerprint: ["hatif-upstream", mutationKey],
      tags: { source: "react-query-mutation", mutationKey, status: String(status) },
    });
    return;
  }

  const normalizedError = normalizeError(error);
  Sentry.captureException(normalizedError, {
    tags: { source: "react-query-mutation" },
    contexts: {
      errorDetails: error && typeof error === "object" ? (error as unknown as Record<string, unknown>) : { rawError: String(error) },
    },
  });
};
